import { useNavigate, NavLink } from "react-router-dom";
import { LogOut, Search, Activity, History as HistoryIcon, BarChart3, Radar, Bookmark, Zap, Briefcase, Beaker } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTradingStyle } from "@/contexts/TradingStyleContext";
import { Button } from "@/components/ui/button";
import StyleToggle from "@/components/StyleToggle";
import MarketStatusChip from "@/components/MarketStatusChip";
import NotificationTray from "@/components/NotificationTray";

export default function TopBar({ onOpenSearch }) {
  const { user, logout } = useAuth();
  const { style } = useTradingStyle() || {};
  const isIntraday = style === "INTRADAY";
  const navigate = useNavigate();
  const navCls = ({ isActive }) =>
    `mono text-[11px] uppercase tracking-widest px-2 py-1 border ${
      isActive
        ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-200"
        : "border-transparent text-slate-400 hover:text-slate-100"
    } flex items-center gap-1`;
  return (
    <header
      data-testid="app-topbar"
      className="sticky top-0 z-40 h-14 bg-[#080B11]/95 backdrop-blur border-b border-[#1E293B] px-4 flex items-center justify-between"
    >
      <div className="flex items-center gap-6">
        <button
          data-testid="app-logo-home"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 group"
        >
          <span className="relative flex h-6 w-6 items-center justify-center border border-cyan-500/40 bg-cyan-500/10">
            <Activity className="h-3.5 w-3.5 text-cyan-400" />
          </span>
          <span className="mono text-sm font-bold tracking-tight text-slate-100 group-hover:text-cyan-300 transition-colors">
            QUANT<span className="text-cyan-400">·</span>SIGNAL
          </span>
          <span className="mono text-[10px] px-1.5 py-0.5 border border-slate-700 text-slate-500 uppercase tracking-widest">
            v1.0 · Phase 4
          </span>
        </button>
        <nav className="hidden md:flex items-center gap-1" data-testid="app-nav">
          {isIntraday && (
            <NavLink to="/what-now" className={navCls} data-testid="nav-whatnow"><Zap className="h-3 w-3" /> What Now</NavLink>
          )}
          {isIntraday && (
            <NavLink to="/trades" className={navCls} data-testid="nav-trades"><Briefcase className="h-3 w-3" /> Trades</NavLink>
          )}
          {isIntraday && (
            <NavLink to="/engine-lab" className={navCls} data-testid="nav-engine-lab"><Beaker className="h-3 w-3" /> Engine Lab</NavLink>
          )}
          <NavLink to="/scanner" className={navCls} data-testid="nav-scanner"><Radar className="h-3 w-3" /> Scanner</NavLink>
          <NavLink to="/watchlists" className={navCls} data-testid="nav-watchlists"><Bookmark className="h-3 w-3" /> Watchlists</NavLink>
          <NavLink to="/history" className={navCls} data-testid="nav-history"><HistoryIcon className="h-3 w-3" /> History</NavLink>
          <NavLink to="/analytics" className={navCls} data-testid="nav-analytics"><BarChart3 className="h-3 w-3" /> Analytics</NavLink>
        </nav>
      </div>
      <div className="flex items-center gap-3">
        <button
          data-testid="open-ticker-search"
          onClick={onOpenSearch}
          className="group flex items-center gap-2 px-3 h-8 border border-[#1E293B] hover:border-[#334155] bg-[#0F1626] hover:bg-[#172036] transition-colors mono text-xs text-slate-400 hover:text-slate-200 min-w-[220px]"
        >
          <Search className="h-3.5 w-3.5" />
          <span>Search ticker…</span>
          <span className="ml-auto flex gap-1">
            <kbd className="px-1.5 py-0.5 border border-slate-700 text-[10px] text-slate-500 mono">⌘</kbd>
            <kbd className="px-1.5 py-0.5 border border-slate-700 text-[10px] text-slate-500 mono">K</kbd>
          </span>
        </button>
        <MarketStatusChip />
        <StyleToggle />
        <NotificationTray />
        {user && user.email && (
          <div className="hidden sm:flex flex-col items-end">
            <span data-testid="topbar-user-email" className="mono text-[11px] text-slate-300 leading-none">
              {user.email}
            </span>
            <span className="mono text-[9px] text-slate-500 uppercase tracking-widest">
              {user.role || "user"}
            </span>
          </div>
        )}
        <Button
          data-testid="logout-button"
          variant="ghost"
          size="sm"
          onClick={logout}
          className="mono text-xs text-slate-400 hover:text-slate-100 h-8 px-2"
        >
          <LogOut className="h-3.5 w-3.5 mr-1" /> Sign out
        </Button>
      </div>
    </header>
  );
}
